import React from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router'
import { useAppSelector } from '../../hooks/redux'

import AgentPage from '../../components/templates/AgentPage'
import SettingsOptions, { OPTIONS } from './SettingsOptions'
import TaskSettings from './TaskSettings'
import NotificationSettings from './NotificationSettings'
import IntegrationSettings from './IntegrationSettings'
import InboxSettings from './InboxSettings'
import RolesPermissionsSettings from './RolesPermissionsSettings'
import SubscriptionSettings from './SubscriptionSettings'
import GeneralSettings from './GeneralSettings'
import UserSettings from './UserSettings'
import TeamSettings from './TeamSettings'
import ExternalChannelsSettings from './ExternalChannelsSettings'
import CustomFieldsSettings from './CustomFieldsSettings'
import TagsSettings from './TagsSettings'

import { Routes } from '@SonarCX/Routes'
import { filterRoutes } from './SettingsOptions/helpers'
import { selectAccess } from '@SonarCX/slices/accessSlice'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useResponsiveSettings } from '@SonarCX/providers/ResponsiveSettingsProvider'
import useUserPermissions from '@SonarCX/hooks/permissions/useUserPermissions'
import useStyles from './styles'

const Settings = () => {
  const { pathname } = useLocation()

  const { loaded } = useAppSelector(selectAccess)

  const hasPermission = useAccess()
  const { isSmall, selecting } = useResponsiveSettings()
  const { hasAdmin } = useUserPermissions()

  const SETTINGS_ROUTES = React.useMemo(() => {
    const opts = {
      [OPTIONS['Custom Fields']]: CustomFieldsSettings,
      [OPTIONS['External Channels']]: ExternalChannelsSettings,
      [OPTIONS.General]: GeneralSettings,
      [OPTIONS.Inboxes]: InboxSettings,
      [OPTIONS.Integrations]: IntegrationSettings,
      [OPTIONS.Notifications]: NotificationSettings,
      [OPTIONS['Roles & Permissions']]: RolesPermissionsSettings,
      [OPTIONS['Subscription & Billing']]: SubscriptionSettings,
      [OPTIONS.Tags]: TagsSettings,
      [OPTIONS.Tasks]: TaskSettings,
      [OPTIONS.Teams]: TeamSettings,
      [OPTIONS.Users]: UserSettings
    }

    if (!loaded) {
      return opts
    }

    const allowedRoutes = Object.values(filterRoutes(hasPermission, hasAdmin))

    // Remove routes that are not allowed
    Object.keys(opts).forEach(opt => {
      if (!allowedRoutes.includes(opt)) {
        delete opts[opt]
      }
    })

    return opts
  }, [loaded, hasPermission, hasAdmin])

  const defaultRoute = React.useMemo(() => {
    if (hasPermission(PERMS.SettingsAdmin)) {
      return OPTIONS.General
    }
    return OPTIONS.Notifications
  }, [hasPermission])

  const classes = useStyles()

  const SELECTOR = <SettingsOptions />

  const PAGES = (
    <div className={classes.root}>
      <Switch>
        {Object.entries(SETTINGS_ROUTES).map(([opt, Component]) => (
          <Route
            key={opt}
            path={`${Routes.SETTINGS}${opt}`}
            component={Component}
          />
        ))}

        {/* Redirects from now hidden routes */}
        <Redirect to={`${Routes.SETTINGS}${defaultRoute}`} />
      </Switch>
    </div>
  )

  return (
    <AgentPage
      scroll
      disableBottomPadding={pathname.endsWith(OPTIONS['Custom Fields'])}
    >
      {/* Redirect from default settings route */}
      {pathname === Routes.SETTINGS && (
        <Redirect to={`${Routes.SETTINGS}${defaultRoute}`} />
      )}

      {isSmall ? selecting && SELECTOR : SELECTOR}

      {isSmall ? !selecting && PAGES : PAGES}
    </AgentPage>
  )
}

export default Settings
